import { useCallback, useMemo, useState, useEffect } from "react"


import { CheckCircle, File, Loader2, Upload, AlertCircle, X, XCircle } from "lucide-react"
import { toast } from "sonner"

import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@ui/dialog"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/tabs"
import { FileUploadStatus, FileUploadAction, FileWithMetadata, DocumentUploadResult, useSmartFileUploadProcessing } from "@/plugins/upload/useSmartFileUploadProcessing"
import { useUserSession } from "@session/UserSession"

import { ContentObjectItem, ContentObjectType } from "@vertesia/common"

enum UploadState {
    ANALYZE = "ANALYZE",
    UPLOAD = "UPLOAD"
}

const NONE = "NONE"
const BATCH_SIZE = 50

interface UploadDocumentsModalprops {
    open: boolean
    collectionId?: string | undefined
    typeId?: string | undefined
    addSkipped?: boolean
    onOpenChange: (open: boolean) => void
    onUploadDone: (uploadedIds: ContentObjectItem[]) => void
}
export function UploadDocumentsModal({ open, collectionId, typeId, addSkipped = false, onOpenChange, onUploadDone }: Readonly<UploadDocumentsModalprops>) {
    const { client, typeRegistry } = useUserSession()
    const { checkDocumentProcessing } = useSmartFileUploadProcessing()

    const [tabState, setTabState] = useState<string>("to_process")
    const [state, setState] = useState<UploadState>(UploadState.ANALYZE)
    const [files, setFiles] = useState<File[]>([])
    const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([])
    const [filesToProcess, setFilesToProcess] = useState<FileWithMetadata[]>([])
    const [filesToCreate, setFilesToCreate] = useState(0)
    const [filesToUpdate, setFilesToUpdate] = useState(0)
    const [filesToSkip, setFilesToSkip] = useState<FileWithMetadata[]>([])
    const [overallProgress, setOverallProgress] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [uploadType, setUploadType] = useState<string>(NONE)
    const [isLoading, setIsLoading] = useState(false)
    const [loadingSkipped, setLoadingSkipped] = useState(false)
    const [filterValue, setFilterValue] = useState("")
    const [loadSkipAfterCompletion, setLoadSkipAfterCompletion] = useState(true)

    const types = useMemo(() => {
        return typeRegistry?.types || ([] as ContentObjectType[])
    }, [typeRegistry?.types])

    const filteredOptions = useMemo(() => {
        return types.filter((type) => type.name.toLowerCase().includes(filterValue.toLowerCase()))
    }, [types, filterValue])

    useEffect(() => {
        if (files.length > 0 && state === UploadState.UPLOAD) {
            setFileStatuses(
                files.map((file) => ({
                    file,
                    status: "pending" as const,
                    progress: 0,
                    action: "create" as const,
                }))
            )
        }
    }, [files, state])

    useEffect(() => {
        if (fileStatuses.length > 0) {
            const completedFiles = fileStatuses.filter((f) => f.status === "success" || f.status === "error").length
            const progress = Math.round((completedFiles / fileStatuses.length) * 100)
            setOverallProgress(progress)
        }
    }, [fileStatuses])

    const resetState = useCallback(() => {
        setFiles([])
        setFileStatuses([])
        setFilesToProcess([])
        setFilesToCreate(0)
        setFilesToUpdate(0)
        setFilesToSkip([])
        setOverallProgress(0)
        setState(UploadState.ANALYZE)
        setUploadType(NONE)
        setIsLoading(false)
        setFilterValue("")
    }, [])

    const handleLoadSkipped = useCallback(async () => {
        if (!addSkipped) {
            return
        }

        setLoadingSkipped(true)
        const results = await Promise.all(filesToSkip.map(async (file) => client.objects.retrieve(file.existingId!)))
        setLoadingSkipped(false)
        onUploadDone(results)
        handleClose()
    }, [filesToSkip])

    const handleClose = useCallback(() => {
        onOpenChange(false)
        resetState()
    }, [onOpenChange, resetState])

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        setFilterValue(e.target.value)
    }, [])

    const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
    }, [])

    const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(true)
    }, [])

    const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(false)
    }, [])

    const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(false)

        const droppedFiles = Array.from(e.dataTransfer.files)
        setFiles((prev) => [...prev, ...droppedFiles])
    }, [])

    const handleUploadOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const selectedFiles = Array.from(event.target.files)
            setFiles((prev) => [...prev, ...selectedFiles])
        }
    }, [])

    const removeFile = useCallback((indexToRemove: number) => {
        setFiles((prev) => prev.filter((_, index) => index !== indexToRemove))
    }, [])

    const processFiles = async () => {
        if (!client || !files?.length) {
            return
        }

        setIsLoading(true)

        try {
            const processed = await checkDocumentProcessing(files, undefined, collectionId)
            const toCreate = processed.filter((f) => f.action === FileUploadAction.CREATE)
            const toUpdate = processed.filter((f) => f.action === FileUploadAction.UPDATE)
            const toSkip = processed.filter((f) => f.action === FileUploadAction.SKIP)

            const toProcess = toCreate.concat(toUpdate)
            setFilesToProcess(toProcess)
            setFilesToCreate(toCreate.length)
            setFilesToUpdate(toUpdate.length)
            setFilesToSkip(toSkip)

            setFiles(files.filter((file) => toCreate.some(f => f.file === file) || toUpdate.some(f => f.file === file)))
            setTabState(toProcess.length > 0 ? "to_process" : toSkip.length > 0 ? "to_skip" : "to_process")
            setState(UploadState.UPLOAD)
        } catch (error) {
            toast.error("Error analyzing files", {
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                duration: 5000,
            })
        } finally {
            setIsLoading(false)
        }
    }

    const updateFileStatus = useCallback((file: File, updates: Partial<FileUploadStatus>) => {
        setFileStatuses((current) => current.map((status) => (status.file === file ? { ...status, ...updates } : status)))
    }, [])

    const processBatch = async (files: FileWithMetadata[], action: "create" | "update", result: DocumentUploadResult) => {
        const batches = []
        for (let i = 0; i < files.length; i += BATCH_SIZE) {
            batches.push(files.slice(i, i + BATCH_SIZE))
        }

        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
            const batch = batches[batchIndex]

            if (files.length > BATCH_SIZE) {
                const processedCount = batchIndex * BATCH_SIZE
                toast.info(`Processing files (${action})`, {
                    description: `Processed ${processedCount}/${files.length} files...`,
                    duration: 2000,
                })
            }

            await Promise.all(
                batch.map(async (fileInfo) => {
                    try {
                        updateFileStatus(fileInfo.file, {
                            status: "uploading",
                            progress: 20,
                        })

                        let obj
                        if (action === "update" && fileInfo.existingId) {
                            obj = await client.store.objects.update(
                                fileInfo.existingId,
                                {
                                    type: typeId ? typeId : uploadType === NONE ? undefined : uploadType,
                                    content: fileInfo.file,
                                    location: fileInfo.location,
                                    name: fileInfo.name,
                                },
                                {
                                    createRevision: true,
                                    revisionLabel: "upload on " + new Date().toISOString(),
                                }
                            )

                            updateFileStatus(fileInfo.file, {
                                status: "success",
                                progress: 100,
                                id: fileInfo.existingId,
                            })

                            result.uploadedFiles.push({
                                id: fileInfo.existingId,
                                name: fileInfo.name,
                                type: typeId ? typeId : uploadType === NONE ? null : uploadType,
                                status: "updated",
                                location: fileInfo.location,
                            })
                        } else {
                            obj = await client.store.objects.create(
                                {
                                    type: typeId ? typeId : uploadType === NONE ? undefined : uploadType,
                                    content: fileInfo.file,
                                    location: fileInfo.location || fileInfo.file.webkitRelativePath || fileInfo.name,
                                },
                                {
                                    collection_id: collectionId,
                                }
                            )

                            updateFileStatus(fileInfo.file, {
                                status: "success",
                                progress: 100,
                                id: obj.id,
                            })

                            result.uploadedFiles.push({
                                id: obj.id,
                                name: fileInfo.name,
                                type: typeId ? typeId : uploadType === NONE ? null : uploadType,
                                status: "created",
                                location: fileInfo.location,
                            })
                        }

                        result.objects.push(obj)
                    } catch (error) {
                        updateFileStatus(fileInfo.file, {
                            status: "error",
                            progress: 100,
                            message: error instanceof Error ? error.message : "Unknown error",
                        })

                        result.failedFiles.push({
                            name: fileInfo.name,
                            error: error instanceof Error ? error.message : "Unknown error",
                            status: "failed",
                            location: fileInfo.location,
                            type: typeId ? typeId : uploadType === NONE ? null : uploadType,
                        })

                        result.success = false
                    }
                })
            )
        }
    }

    const onUpload = async () => {
        if (!client || !filesToProcess?.length) {
            return
        }

        setIsLoading(true)

        const result: DocumentUploadResult = {
            success: true,
            objects: [],
            uploadedFiles: [],
            skippedFiles: [],
            failedFiles: [],
        }

        try {
            const toUpdate = filesToProcess.filter((f) => f.action === FileUploadAction.UPDATE)
            const toCreate = filesToProcess.filter((f) => f.action === FileUploadAction.CREATE)
            let skippedFiles: ContentObjectItem[] = []

            if (toUpdate.length > 0) {
                await processBatch(toUpdate, "update", result)
            }

            if (toCreate.length > 0) {
                await processBatch(toCreate, "create", result)
            }
            if (addSkipped && loadSkipAfterCompletion && filesToSkip.length > 0) {
                skippedFiles = await Promise.all(filesToSkip.map(async (file) => client.objects.retrieve(file.existingId!)))
            }

            setFileStatuses((current) => {
                const pendingFiles = current.filter((status) => status.status === "pending")

                if (pendingFiles.length > 0) {
                    pendingFiles.forEach((status) => {
                        result.failedFiles.push({
                            name: status.file.name,
                            error: "Upload process interrupted",
                            status: "failed",
                            type: typeId ? typeId : uploadType === NONE ? null : uploadType,
                        })
                    })
                    result.success = false
                }

                return current.map((status) => (status.status === "pending" ? { ...status, status: "error" as const, progress: 100, message: "Upload process interrupted" } : status))
            })

            const createdCount = result.uploadedFiles.filter((f) => f.status === "created").length
            const updatedCount = result.uploadedFiles.filter((f) => f.status === "updated").length
            const skippedCount = result.skippedFiles.length
            const failedCount = result.failedFiles.length

            const statusParts = []
            if (createdCount > 0) {
                statusParts.push(`${createdCount} file${createdCount !== 1 ? "s" : ""} uploaded`)
            }
            if (updatedCount > 0) {
                statusParts.push(`${updatedCount} file${updatedCount !== 1 ? "s" : ""} updated`)
            }
            if (skippedCount > 0) {
                statusParts.push(`${skippedCount} file${skippedCount !== 1 ? "s" : ""} skipped`)
            }
            if (failedCount > 0) {
                statusParts.push(`${failedCount} file${failedCount !== 1 ? "s" : ""} failed`)
            }

            const statusMessage = statusParts.join(", ")

            if (failedCount > 0) {
                toast.warning("Upload Complete", {
                    description: statusMessage,
                    duration: 5000,
                })
            } else {
                toast.success("Upload Complete", {
                    description: statusMessage,
                    duration: 5000,
                })
            }

            const allUploaded = result.objects.concat(skippedFiles)

            onUploadDone(allUploaded)
            handleClose()
        } catch (error) {
            toast.error("Upload failed", {
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                duration: 5000,
            })
        } finally {
            setIsLoading(false)
        }
    }

    const renderFileList = () => (
        <div className="space-y-2">
            <Label className="text-sm font-medium">Selected Files:</Label>
            <div className="max-h-48 overflow-y-auto space-y-1">
                {
                    files.map((file, index) => (
                        <div
                            key={`${file.name}-${index}`}
                            className="flex items-center p-2 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors cursor-pointer group w-full overflow-hidden"
                            onClick={() => removeFile(index)}
                            title="Click to remove file"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault()
                                    removeFile(index)
                                }
                            }}
                        >
                            <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                                <span className="text-sm text-gray-700 truncate block min-w-0 flex-1">{file.name}</span>
                                <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                            </div>
                            <X className="h-4 w-4 text-gray-400 group-hover:text-red-500 transition-colors flex-shrink-0 ml-2" />
                        </div>
                    ))
                }
            </div>
            <p className="text-xs text-gray-500">Click on any file to remove it from the list</p>
        </div>
    )

    const renderAnalysisResults = () => (
        <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
                <div className="text-lg font-medium">File Analysis Results</div>
                <div className="text-sm text-muted-foreground">
                    {files.length} file{files.length === 1 ? "" : "s"}
                </div>
            </div>
            <div className="bg-color-muted/10 p-4 rounded-md">
                <div className="grid grid-cols-3 gap-4">
                    <div className="flex flex-col items-center">
                        <div className="flex items-center gap-2">
                            <Upload className="h-5 w-5 text-blue-500" />
                            <span className="font-medium">New</span>
                        </div>
                        <div className="text-2xl font-semibold">{filesToCreate}</div>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <span className="font-medium">Update</span>
                        </div>
                        <div className="text-2xl font-semibold">{filesToUpdate}</div>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-yellow-500" />
                            <span className="font-medium">Skip</span>
                        </div>
                        <div className="text-2xl font-semibold">{filesToSkip.length}</div>
                    </div>
                </div>
            </div>
        </div>
    )

    const renderUploadProgress = () => (
        <>
            <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                    <div className="text-lg font-medium">Uploading Files</div>
                    <div className="text-sm text-muted-foreground">{overallProgress}% complete</div>
                </div>
                <div className="h-2 bg-color-muted/20 rounded-full overflow-hidden">
                    <div className="h-full bg-color-primary rounded-full transition-all duration-300" style={{ width: `${overallProgress}%` }} />
                </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
                {
                    fileStatuses.map((fileStatus, index) => (
                        <div key={`${fileStatus.file.name}-${index}`} className="flex items-center py-2 border-b border-color-border last:border-b-0">
                            <div className="mr-3">
                                {fileStatus.status === "pending" && <File className="h-5 w-5 text-muted-foreground" />}
                                {fileStatus.status === "uploading" && <Loader2 className="h-5 w-5 animate-spin text-blue-500" />}
                                {fileStatus.status === "success" && <CheckCircle className="h-5 w-5 text-green-500" />}
                                {fileStatus.status === "error" && <XCircle className="h-5 w-5 text-red-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="truncate font-medium">{fileStatus.file.name}</div>
                                <div className="text-xs text-muted-foreground">
                                    {fileStatus.status === "pending" && "Waiting..."}
                                    {fileStatus.status === "uploading" && "Uploading..."}
                                    {fileStatus.status === "success" && (fileStatus.action === "create" ? "Created" : fileStatus.action === "update" ? "Updated" : "Skipped")}
                                    {fileStatus.status === "error" && fileStatus.message}
                                </div>
                            </div>
                        </div>
                    ))
                }
            </div>
        </>
    )

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Upload Content to Vertesia</DialogTitle>
                </DialogHeader>
                {
                    state === UploadState.ANALYZE && (
                        <div className="space-y-4">
                            <Label>Number of files: {files.length}</Label>
                            <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center transition-colors duration-200 ease-in-out ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}`} role="button" tabIndex={0} aria-label="File upload area">
                                <p className="text-sm text-gray-500">
                                    <Button variant="link" className="text-primary-dark underline hover:opacity-60 px-1" onClick={() => document.getElementById("file-upload")?.click()}>
                                        Upload file(s)
                                    </Button>
                                    or drag and drop
                                </p>
                                <input id="file-upload" type="file" className="hidden" multiple onChange={handleUploadOnChange} aria-label="Select files to upload" />
                            </div>
                            {files.length > 0 && renderFileList()}
                        </div>
                    )
                }
                {
                    state === UploadState.UPLOAD && (
                        <div className="space-y-4">
                            {renderAnalysisResults()}
                            <Tabs value={tabState} onValueChange={setTabState} className="flex-1 flex flex-col min-h-0">
                                <div className="flex-shrink-0">
                                    <TabsList className="grid grid-cols-2 gap-1">
                                        <TabsTrigger value="to_process" disabled={filesToProcess.length === 0} className="data-[state=active]:bg-white! hover:data-[state=active]:bg-gray-200! hover:data-[state=inactive]:bg-white! data-[state=active]:border-gray-300!">To Process</TabsTrigger>
                                        <TabsTrigger value="to_skip" disabled={filesToSkip.length === 0} className="data-[state=active]:bg-white! hover:data-[state=active]:bg-gray-200! hover:data-[state=inactive]:bg-white! data-[state=active]:border-gray-300!">To Skip</TabsTrigger>
                                    </TabsList>
                                </div>
                                <div className="flex-1 flex flex-col min-h-0">
                                    <TabsContent value="to_process" className="flex-1 flex flex-col gap-4 min-h-0 mt-0">
                                        <div className="gap-1 max-h-48 overflow-auto space-y-1">
                                            {
                                                filesToProcess.map((fs, index) => (
                                                    <div key={`${fs.file.name}-${index}`} className={"flex items-center p-2 rounded-md transition-colors cursor-pointer group w-full overflow-hidden border bg-blue-100 border-blue-200"}>
                                                        <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                                                            <span className="text-sm text-gray-700 truncate block min-w-0 flex-1">
                                                                {fs.file.name}
                                                            </span>
                                                            <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                                                                ({(fs.file.size / 1024 / 1024).toFixed(2)} MB)
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        {
                                            (!typeId && filesToProcess.length > 0) && (
                                                <div className="flex flex-col gap-2">
                                                    <Label>Upload Type</Label>
                                                    <Select onValueChange={setUploadType} defaultValue={NONE}>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select an Upload Type" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {
                                                                types.length > 5 && (
                                                                    <div className="p-2">
                                                                        <Input placeholder="Search Types..." value={filterValue} onChange={handleInputChange} onKeyDown={handleInputKeyDown} onClick={(e) => e.stopPropagation()} />
                                                                    </div>
                                                                )
                                                            }
                                                            <SelectGroup className="max-h-[200px] overflow-y-auto">
                                                                <SelectItem value={NONE}>Let Vertesia Choose</SelectItem>
                                                                {
                                                                    filteredOptions.map((type) => (
                                                                        <SelectItem key={type.id} value={type.id}>
                                                                            {type.name}
                                                                        </SelectItem>
                                                                    ))
                                                                }
                                                            </SelectGroup>
                                                        </SelectContent>
                                                    </Select>
                                                    <div className="flex flex-col text-sm text-blue-600 gap-1">
                                                        <div className="flex items-center gap-2 font-bold">
                                                            <CheckCircle className="size-4" />
                                                            <span>Type selection is optional.</span>
                                                        </div>
                                                        <span>Leave empty to let Vertesia choose the most appropriate type</span>
                                                        <span>This is recommended for most uploads and ensures optimal processing.</span>
                                                    </div>
                                                </div>
                                            )
                                        }
                                        {isLoading && renderUploadProgress()}
                                    </TabsContent>
                                    <TabsContent value="to_skip" className="flex-1 min-h-0 mt-0">
                                        <div className="gap-1 max-h-48 overflow-auto space-y-1">
                                            {
                                                filesToSkip.map((fs, index) => (
                                                    <div key={`${fs.file.name}-${index}`} className={"flex items-center p-2 rounded-md transition-colors cursor-pointer group w-full overflow-hidden border bg-blue-100 border-blue-200"}>
                                                        <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                                                            <span className="text-sm text-gray-700 truncate block min-w-0 flex-1">
                                                                {fs.file.name}
                                                            </span>
                                                            <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                                                                ({(fs.file.size / 1024 / 1024).toFixed(2)} MB)
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </TabsContent>
                                </div>
                            </Tabs>
                        </div>
                    )
                }
                <DialogFooter>
                    <div className="flex justify-between items-center w-full">
                        <div className="flex gap-4 items-center">
                            {
                                (addSkipped && state === UploadState.UPLOAD && filesToProcess.length > 0 && filesToSkip.length > 0) && (
                                    <>

                                        <Label className="flex items-center gap-1">
                                            <Checkbox
                                                id="enable-skip-load"
                                                checked={loadSkipAfterCompletion}
                                                onCheckedChange={(checked) => setLoadSkipAfterCompletion(checked === true)}
                                            /> 
                                            <p className="leading-none font-medium">
                                                Add skipped files to selection after upload completion
                                            </p>
                                        </Label>        
                                    </>
                                )
                            }
                        </div>
                        <div className="flex gap-4">
                            <Button variant="destructive" onClick={handleClose}>
                                Cancel
                            </Button>
                            {
                                state === UploadState.ANALYZE && (
                                    <Button variant="outline" onClick={processFiles} disabled={!files.length || isLoading}>
                                        {isLoading ? "Analyzing..." : "Analyze"}
                                    </Button>
                                )
                            }
                            {
                                (addSkipped && state === UploadState.UPLOAD && !filesToProcess.length && filesToSkip.length > 0) && (
                                    <Button variant="outline" onClick={handleLoadSkipped} disabled={loadingSkipped}>
                                        {
                                            loadingSkipped ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Adding to selection...
                                                </>
                                            ) : (
                                                `Add to selection (${filesToSkip.length})`
                                            )
                                        }
                                    </Button>
                                )
                            }
                            {
                                (state === UploadState.UPLOAD && filesToProcess.length > 0) && (
                                    <Button variant="outline" onClick={onUpload} disabled={isLoading}>
                                        {isLoading ? "Uploading..." : "Upload"}
                                    </Button>
                                )
                            }
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
